import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Fuel, FuelUnit } from "@/types/fuels/fuels.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { FuelForm } from "./fuels/FuelForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useFuelTable } from "@/hooks/fuels/useFuelTable";
import { useFuelColumns } from "./fuels/columns";
import { useConfirmationDialog } from "@/hooks/fuels/useConfirmationDialog";

export const Fuels = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedFuel, setSelectedFuel] = useState<Fuel | null>(null);

  const {
    fuels,
    isLoading,
    refetch,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    nameFilters,
    setNameFilters,
    unitFilters,
    setUnitFilters,
    clearFilters,
    nameOptions,
    unitOptions,
  } = useFuelTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog({ onSuccess: refetch });

  const handleAddFuel = () => {
    setSelectedFuel(null);
    setFormOpen(true);
  };

  const handleEditFuel = (fuel: Fuel) => {
    setSelectedFuel(fuel);
    setFormOpen(true);
  };

  const handleArchiveAction = (action: { type: "archive" | "restore"; fuelId: string; fuelName: string }) => {
    openDialog(action.type, action.fuelId, action.fuelName);
  };

  const columns = useFuelColumns({ t, onEdit: handleEditFuel, onArchiveAction: handleArchiveAction });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("fuels.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: (values: string[]) => setNameFilters(values),
      showSearch: true,
    },
    {
      label: t("fuels.filters.unit"),
      options: unitOptions,
      selectedValues: unitFilters,
      onChange: (values: string[]) => setUnitFilters(values as FuelUnit[]),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("fuels.title")}</h2>
        {!isAssimagraUser && (
          <Button onClick={handleAddFuel} className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto">
            <Plus className="h-4 w-4 mr-2" />
            {t("fuels.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("fuels.searchPlaceholder")}
        filters={filters}
        toggle={[
          {
            label: t("fuels.showArchived"),
            checked: showArchived,
            onCheckedChange: setShowArchived,
          },
        ]}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={fuels} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <FuelForm isOpen={formOpen} onClose={() => setFormOpen(false)} fuelToEdit={selectedFuel} onSuccess={refetch} />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={() => handleConfirm(confirmAction)}
          title={confirmAction.type === "archive" ? t("fuels.confirmArchive.title") : t("fuels.confirmRestore.title")}
          description={t(
            confirmAction.type === "archive" ? "fuels.confirmArchive.description" : "fuels.confirmRestore.description",
            { name: confirmAction.fuelName }
          )}
          confirmText={
            isConfirmActionLoading
              ? t(
                  confirmAction.type === "archive"
                    ? "fuels.confirmArchive.actionLoading"
                    : "fuels.confirmRestore.actionLoading"
                )
              : t(confirmAction.type === "archive" ? "fuels.confirmArchive.action" : "fuels.confirmRestore.action")
          }
          cancelText={t("fuels.cancel")}
          variant={confirmAction.type === "archive" ? "destructive" : "default"}
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
